import axios, { AxiosInstance, AxiosResponse } from 'axios';
import type {
  ApiResponse,
  LoginRequest,
  LoginResponse,
  DashboardData,
  AnswerSubmissionRequest,
  AnswerSubmissionResponse,
  RankingEntry,
  GameStats,
} from '@/types';

class ApiService {
  private api: AxiosInstance;
  private baseURL: string;

  constructor() {
    // In production, use relative URLs (nginx proxies /api/ to backend)
    // In development, use VITE_API_URL or localhost
    const envUrl = import.meta.env?.VITE_API_URL;
    this.baseURL = envUrl !== undefined ? envUrl : (import.meta.env?.DEV ? 'http://127.0.0.1:8000' : '');
    
    this.api = axios.create({
      baseURL: this.baseURL,
      timeout: 10000,
      headers: {
        'Content-Type': 'application/json',
      },
      withCredentials: true, // Important for session-based auth
    });

    this.setupInterceptors();
  }

  private setupInterceptors() {
    // Request interceptor
    this.api.interceptors.request.use(
      (config) => {
        // Add CSRF token if available
        const csrfToken = this.getCsrfToken();
        if (csrfToken) {
          config.headers['X-CSRFToken'] = csrfToken;
        }

        // Add auth token if available (if using token-based auth)
        const token = localStorage.getItem('auth_token');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }

        return config;
      },
      (error) => Promise.reject(error)
    );

    // Response interceptor
    this.api.interceptors.response.use(
      (response: AxiosResponse) => response,
      (error) => {
        if (error.response?.status === 401) {
          const errorData = error.response?.data;
          // Check for session invalidated (single-device enforcement)
          if (errorData?.code === 'SESSION_INVALIDATED' || errorData?.error === 'session_invalidated') {
            this.handleSessionInvalidated(errorData?.message || 'Sesja została zakończona - zalogowano się z innego urządzenia.');
          } else {
            // Handle other unauthorized access
            this.handleUnauthorized();
          }
        }
        return Promise.reject(error);
      }
    );
  }

  private handleSessionInvalidated(message: string) {
    localStorage.removeItem('auth_token');
    localStorage.removeItem('game_session');
    // Show alert to user before redirecting
    alert(message);
    window.location.href = '/';
  }

  private getCsrfToken(): string | null {
    const name = 'csrftoken';
    const cookies = document.cookie.split(';');
    for (let cookie of cookies) {
      const [cookieName, cookieValue] = cookie.trim().split('=');
      if (cookieName === name) {
        return decodeURIComponent(cookieValue);
      }
    }
    return null;
  }

  private handleUnauthorized() {
    localStorage.removeItem('auth_token');
    // Redirect to login or emit an event
    window.location.href = '/';
  }

  // Authentication endpoints
  async login(credentials: LoginRequest): Promise<LoginResponse> {
    try {
      const response = await this.api.post<LoginResponse>('/api/v1/auth/login/', credentials);
      
      // Store token if provided
      if (response.data.token) {
        localStorage.setItem('auth_token', response.data.token);
      }
      
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Login failed');
    }
  }

  async logout(): Promise<void> {
    try {
      await this.api.post('/api/v1/auth/logout/');
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      localStorage.removeItem('auth_token');
    }
  }

  // Dashboard endpoint
  async getDashboard(): Promise<DashboardData> {
    try {
      const response = await this.api.get<DashboardData>('/api/v1/dashboard/');
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to load dashboard');
    }
  }

  // Answer submission endpoint
  async submitAnswer(submission: AnswerSubmissionRequest): Promise<AnswerSubmissionResponse> {
    try {
      const response = await this.api.post<AnswerSubmissionResponse>('/api/v1/submit-answer/', submission);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to submit answer');
    }
  }

  // Stop clock endpoint - team enters 6-digit code to stop their timer
  async stopClock(stopCode: string): Promise<{ success: boolean; error?: string; final_time?: string; finish_time?: string }> {
    try {
      const response = await this.api.post('/api/v1/stop-game/', { stop_code: stopCode });
      return response.data;
    } catch (error: any) {
      // Return error from backend response
      if (error.response?.data?.error) {
        return { success: false, error: error.response.data.error };
      }
      throw new Error(error.response?.data?.message || 'Failed to stop clock');
    }
  }

  // Legacy alias for stopClock
  async stopGame(): Promise<ApiResponse> {
    throw new Error('Use stopClock(code) instead');
  }

  // Movement diagram data endpoint
  async getMovementDiagram(): Promise<{
    success: boolean;
    optimal_route: number[];
    team_route: number[];
    route_efficiency: number | null;
    solve_times: Array<{
      tile_number: number;
      solved_at: string | null;
      minutes_from_start: number | null;
      attempts: number;
    }>;
    all_tiles: number[];
    total_tiles: number;
    solved_count: number;
    team_name: string;
    game_name: string;
    is_finished: boolean;
    final_time: string | null;
  }> {
    try {
      const response = await this.api.get('/api/v1/movement-diagram/');
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to load movement diagram');
    }
  }

  // Ranking endpoint
  async getRanking(gameId: number): Promise<RankingEntry[]> {
    try {
      const response = await this.api.get<RankingEntry[]>(`/api/v1/games/${gameId}/ranking/`);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to load ranking');
    }
  }

  // Statistics endpoint
  async getGameStats(gameId: number): Promise<GameStats> {
    try {
      const response = await this.api.get<GameStats>(`/api/v1/games/${gameId}/stats/`);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to load game statistics');
    }
  }

  // Heartbeat endpoint
  async heartbeat(): Promise<ApiResponse> {
    try {
      const response = await this.api.post<ApiResponse>('/api/v1/heartbeat/');
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Heartbeat failed');
    }
  }

  // Health check
  async checkConnection(): Promise<boolean> {
    try {
      await this.heartbeat();
      return true;
    } catch {
      return false;
    }
  }

  // Phone verification endpoint
  async verifyPhone(phoneNumber: string): Promise<{
    success: boolean;
    message?: string;
    phone_number?: string;
    phone_verified?: boolean;
    error?: string;
  }> {
    try {
      const response = await this.api.post('/api/v1/verify-phone/', { phone_number: phoneNumber });
      return response.data;
    } catch (error: any) {
      // Return error from backend response
      if (error.response?.data) {
        const errorData = error.response.data;
        return {
          success: false,
          error: errorData.phone_number?.[0] || errorData.error || 'Błąd weryfikacji numeru'
        };
      }
      throw new Error(error.response?.data?.message || 'Failed to verify phone');
    }
  }

  // Team photo upload endpoint
  async uploadTeamPhoto(formData: FormData): Promise<{
    success: boolean;
    message?: string;
    photo_url?: string;
    error?: string;
  }> {
    try {
      const response = await this.api.post('/api/v1/upload-team-photo/', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    } catch (error: any) {
      // Return error from backend response
      if (error.response?.data) {
        const errorData = error.response.data;
        return {
          success: false,
          error: errorData.error || 'Błąd przesyłania zdjęcia'
        };
      }
      throw new Error(error.response?.data?.message || 'Failed to upload photo');
    }
  }

  // Admin endpoints
  async getAdminGames(): Promise<any[]> {
    try {
      const response = await this.api.get('/api/v1/admin/games/');
      return response.data.results || response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to load games');
    }
  }

  async getAdminTeams(gameId?: number): Promise<any[]> {
    try {
      const url = gameId ? `/api/v1/admin/teams/?game=${gameId}` : '/api/v1/admin/teams/';
      const response = await this.api.get(url);
      return response.data.results || response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to load teams');
    }
  }

  async startGame(gameId: number): Promise<ApiResponse> {
    try {
      const response = await this.api.post<ApiResponse>(`/api/v1/admin/games/${gameId}/start_game/`);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to start game');
    }
  }

  async endGame(gameId: number): Promise<ApiResponse> {
    try {
      const response = await this.api.post<ApiResponse>(`/api/v1/admin/games/${gameId}/end_game/`);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to end game');
    }
  }

  // =============================================================================
  // Chat API endpoints
  // =============================================================================

  async getChatMessages(): Promise<{
    success: boolean;
    messages?: Array<{
      id: number;
      sender_type: 'team' | 'operator';
      content: string;
      image_url: string | null;
      is_read: boolean;
      created_at: string;
    }>;
    error?: string;
  }> {
    try {
      const response = await this.api.get('/api/v1/chat/messages/');
      return response.data;
    } catch (error: any) {
      if (error.response?.data) {
        return { success: false, error: error.response.data.error };
      }
      throw new Error(error.response?.data?.message || 'Failed to load messages');
    }
  }

  async sendChatMessage(content: string, image?: File): Promise<{
    success: boolean;
    message?: {
      id: number;
      sender_type: 'team' | 'operator';
      content: string;
      image_url: string | null;
      is_read: boolean;
      created_at: string;
    };
    error?: string;
  }> {
    try {
      const formData = new FormData();
      formData.append('content', content);
      if (image) {
        formData.append('image', image);
      }

      const response = await this.api.post('/api/v1/chat/send/', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    } catch (error: any) {
      if (error.response?.data) {
        return { success: false, error: error.response.data.error };
      }
      throw new Error(error.response?.data?.message || 'Failed to send message');
    }
  }

  async markChatMessagesRead(): Promise<{
    success: boolean;
    marked_read?: number;
    error?: string;
  }> {
    try {
      const response = await this.api.post('/api/v1/chat/mark-read/');
      return response.data;
    } catch (error: any) {
      if (error.response?.data) {
        return { success: false, error: error.response.data.error };
      }
      throw new Error(error.response?.data?.message || 'Failed to mark messages as read');
    }
  }

  async getChatUnreadCount(): Promise<{
    unread_count: number;
  }> {
    try {
      const response = await this.api.get('/api/v1/chat/unread-count/');
      return response.data;
    } catch (error: any) {
      return { unread_count: 0 };
    }
  }
}

// Export singleton instance
export const apiService = new ApiService();
export default apiService;